import { Head, Link } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { SiteLayout } from '@/components/site-layout'
import { Button } from '@/components/ui/button'
import { BaselineAppModal } from '@/components/baseline-app-modal'
import { apiFetch } from '@/lib/utils'

// ── Types ────────────────────────────────────────────────────────────────────

type Nominee = {
  id: string
  slug: string
  name: string
  votes_count: number
  photo_url: string
}

type Category = {
  id: string
  title: string
  votes_count: number
  icon_url?: string
  nominees: Nominee[]
}

type Section = {
  id: string
  title: string
  position: number
  sponsor: boolean
  categories: Category[]
  nominees?: Nominee[]
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CAMPAIGN_ID = 'e9b2ccf3-fb45-4be8-9d4a-b017fa37586b'
const API_BASE = 'https://baseline.live/api/v2'

// ── Helpers ───────────────────────────────────────────────────────────────────

// ── Sub-components ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="flex flex-col gap-2">
      <div className="aspect-square w-full animate-pulse rounded-2xl bg-[#042133]/8" />
      <div className="h-3 w-3/4 animate-pulse rounded-full bg-[#042133]/8" />
      <div className="h-3 w-1/2 animate-pulse rounded-full bg-[#042133]/8" />
    </div>
  )
}

function NomineeCard({ nominee, rank, objectContain, showRank = true }: { nominee: Nominee; rank: number; objectContain?: boolean; showRank?: boolean }) {
  const badgeBg =
    rank === 1 ? 'bg-[#FCC632] text-[#042133]' :
    rank === 2 ? 'bg-[#b0b8c1] text-white' :
    rank === 3 ? 'bg-[#cd7f32] text-white' :
    'bg-[#042133]/60 text-white'

  return (
    <Link
      href={`/de-cu/${nominee.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[#042133]/10 bg-white/90 shadow-sm transition hover:shadow-md hover:border-[#042133]/20 justify-between"
    >
      <div className="px-3 pt-3 pb-2.5 sm:px-4 sm:pt-4">
        <p className="line-clamp-2 font-semibold text-[#042133] text-xs leading-snug sm:text-sm">
          {nominee.name}
        </p>
        {showRank && <p className="mt-0.5 text-[10px] text-[#5b7788]">{nominee.votes_count} phiếu</p>}
      </div>
      <div className="relative aspect-square w-full overflow-hidden bg-[#042133]/5">
        {showRank && (
          <span className={`absolute left-2 top-2 z-10 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${badgeBg}`}>
            #{rank}
          </span>
        )}
        {nominee.photo_url ? (
          <img
            src={nominee.photo_url}
            alt={nominee.name}
            className={`h-full w-full transition duration-300 group-hover:scale-105 ${objectContain ? 'object-contain p-2' : 'object-cover'}`}
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[#5b7788] text-4xl">
            🏆
          </div>
        )}
      </div>
    </Link>
  )
}

function CategoryBlock({
  category,
  onVoteClick,
}: {
  category: Category
  onVoteClick: () => void
}) {
  return (
    <div className="rounded-2xl border border-[#042133]/10 bg-white/80 p-4 shadow-[0_10px_40px_-20px_rgba(4,33,51,0.12)] sm:p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-[#042133] text-sm leading-snug sm:text-base">
            {category.title}
          </h3>
        </div>
        <Button
          onClick={onVoteClick}
          className="shrink-0 rounded-full bg-[#FCC632] px-3 py-1.5 font-semibold text-[#042133] text-[10px] uppercase tracking-[0.1em] hover:bg-[#FCC632]/90"
        >
          <span className="sm:hidden">Bình chọn</span><span className="hidden sm:inline">Bình chọn tại Baseline ▶</span>
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {(() => {
          const sorted = [...category.nominees].sort((a, b) => b.votes_count - a.votes_count)
          return sorted.map((nominee, i) => {
            const rank = sorted.findIndex(n => n.votes_count === nominee.votes_count) + 1
            return <NomineeCard key={nominee.id} nominee={nominee} rank={rank} />
          })
        })()}
      </div>
    </div>
  )
}

function SponsorSectionBlock({
  section,
  index,
  onVoteClick,
}: {
  section: Section
  index: number
  onVoteClick: () => void
}) {
  const numeral = ['I', 'II', 'III', 'IV', 'V'][index] ?? String(index + 1)
  const nominees = section.nominees ?? []
  const sorted = [...nominees].sort((a, b) => b.votes_count - a.votes_count)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#042133] font-bold text-[10px] text-white">
          {numeral}
        </span>
        <h2 className="font-semibold text-[#042133] text-sm uppercase tracking-[0.15em] sm:text-base">
          {section.title}
        </h2>
      </div>
      <div className="rounded-2xl border border-[#042133]/10 bg-white/80 p-4 shadow-[0_10px_40px_-20px_rgba(4,33,51,0.12)] sm:p-5">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {sorted.map((nominee, i) => {
            const rank = sorted.findIndex((n) => n.votes_count === nominee.votes_count) + 1
            return <NomineeCard key={nominee.id} nominee={nominee} rank={rank} objectContain showRank={false} />
          })}
        </div>
      </div>
    </div>
  )
}

function SectionBlock({
  section,
  index,
  onVoteClick,
}: {
  section: Section
  index: number
  onVoteClick: () => void
}) {
  if (section.sponsor) {
    return <SponsorSectionBlock section={section} index={index} onVoteClick={onVoteClick} />
  }

  const numeral = ['I', 'II', 'III', 'IV', 'V'][index] ?? String(index + 1)
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#042133] font-bold text-[10px] text-white">
          {numeral}
        </span>
        <h2 className="font-semibold text-[#042133] text-sm uppercase tracking-[0.15em] sm:text-base">
          {section.title}
        </h2>
      </div>
      <div className="space-y-4">
        {section.categories.map((cat) => (
          <CategoryBlock key={cat.id} category={cat} onVoteClick={onVoteClick} />
        ))}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BinhChonIndex() {
  const [sections, setSections] = useState<Section[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    async function loadData() {
      try {
        // Step 1: Fetch campaign → sections list
        const campaignRes = await apiFetch(`${API_BASE}/voting_campaigns/${CAMPAIGN_ID}`)
        if (!campaignRes.ok) throw new Error('Không thể tải dữ liệu chiến dịch.')
        const campaign = await campaignRes.json()

        type RawNominee = { id: string; slug: string; name: string; votes_count: number; photo_url: string }
        type RawCat = { id: string; title: string; votes_count: number; icon_url?: string }
        type RawSection = { id: string; title: string; position: number; sponsor?: boolean; voting_nominees?: RawNominee[]; voting_categories?: RawCat[] }

        const rawSections: RawSection[] =
          campaign.voting_sections ?? campaign.sections ?? campaign.data?.sections ?? []

        // Step 2: Fetch all sections in parallel → categories or nominees (sponsor)
        const sectionDetails = await Promise.all(
          rawSections.map(async (s) => {
            // Sponsor sections: use nominees already in campaign response, or fetch if missing
            if (s.sponsor) {
              if (s.voting_nominees && s.voting_nominees.length > 0) {
                return { ...s, rawCats: [] as RawCat[], rawNominees: s.voting_nominees }
              }
              const res = await apiFetch(`${API_BASE}/voting_sections/${s.id}`)
              if (!res.ok) return { ...s, rawCats: [] as RawCat[], rawNominees: [] as RawNominee[] }
              const data = await res.json()
              const rawNominees: RawNominee[] = data.voting_nominees ?? data.nominees ?? data.data?.nominees ?? []
              return { ...s, rawCats: [] as RawCat[], rawNominees }
            }

            const res = await apiFetch(`${API_BASE}/voting_sections/${s.id}`)
            if (!res.ok) return { ...s, rawCats: [] as RawCat[], rawNominees: [] as RawNominee[] }
            const data = await res.json()
            const isSponsor = data.sponsor ?? false
            if (isSponsor) {
              const rawNominees: RawNominee[] = data.voting_nominees ?? data.nominees ?? data.data?.nominees ?? []
              return { ...s, sponsor: true, rawCats: [] as RawCat[], rawNominees }
            }

            const rawCats: RawCat[] = data.voting_categories ?? data.categories ?? data.data?.categories ?? []
            return { ...s, sponsor: false, rawCats, rawNominees: [] as RawNominee[] }
          })
        )

        type SectionWithRaw = RawSection & { rawCats: RawCat[]; rawNominees: RawNominee[] }

        // Step 3: For non-sponsor sections, fetch categories → nominees
        const sectionsWithNominees: Section[] = await Promise.all(
          (sectionDetails as SectionWithRaw[]).map(async (s) => {
            if (s.sponsor) {
              return {
                id: s.id,
                title: s.title,
                position: s.position,
                sponsor: true,
                categories: [],
                nominees: s.rawNominees,
              }
            }

            const rawCats = s.rawCats ?? []
            const categories: Category[] = await Promise.all(
              rawCats.map(async (cat) => {
                const res = await apiFetch(`${API_BASE}/voting_categories/${cat.id}`)
                if (!res.ok) return { ...cat, nominees: [] }
                const data = await res.json()
                const rawNominees: Array<{ id: string; slug: string; name: string; votes_count: number; photo_url: string }> =
                  data.voting_nominees ?? data.nominees ?? data.data?.nominees ?? []
                return {
                  ...cat,
                  nominees: rawNominees,
                }
              })
            )
            return {
              id: s.id,
              title: s.title,
              position: s.position,
              sponsor: false,
              categories,
            }
          })
        )

        setSections(sectionsWithNominees.sort((a, b) => a.position - b.position))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Đã có lỗi xảy ra.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  return (
    <SiteLayout>
      <Head title="Đề cử chính thức — Vietnam Pickleball Awards 2025">
        <meta
          name="description"
          content="Xem danh sách đề cử chính thức và bình chọn trên app Baseline. Vietnam Pickleball Awards 2025 — Bình chọn từ 07/03 đến 20/03/2026."
        />
      </Head>

      {/* Page header */}
      <section className="mx-auto flex max-w-5xl flex-col items-center px-4 pt-6 text-center sm:px-6 sm:pt-8">
        <p className="text-[#5b7788] text-[10px] uppercase tracking-[0.3em]">
          07/03 – 20/03/2026
        </p>
        <h1 className="mt-2 font-display font-semibold text-2xl tracking-tight sm:text-3xl md:text-4xl">
          Đề cử chính thức
        </h1>
        <p className="mt-2 max-w-xxl text-[#5b7788] text-sm leading-relaxed">
          Danh sách các đề cử chính thức mỗi hạng mục giải thưởng đã chính thức được công bố.
          <br></br>
          Bình chọn cho đề cử mình yêu thích nhất tại Baseline.
          <br></br>
          Đêm Gala vinh danh trao giải sẽ được diễn ra vào ngày 23/03/2026 tại thành phố Hồ Chí Minh.
        </p>
        <Button
          onClick={() => setModalOpen(true)}
          className="mt-4 rounded-full bg-[#FCC632] px-6 py-2 font-semibold text-[#042133] text-sm uppercase tracking-[0.15em] hover:bg-[#FCC632]/90"
        >
          Tải app Baseline để bình chọn
        </Button>
      </section>

      {/* Content */}
      <section className="mx-auto mt-8 max-w-5xl space-y-10 px-4 pb-20 sm:mt-10 sm:px-6">
        {loading && (
          <div className="space-y-10">
            {[0, 1, 2].map((si) => (
              <div key={si} className="space-y-4">
                <div className="h-5 w-48 animate-pulse rounded-full bg-[#042133]/8" />
                {[0, 1].map((ci) => (
                  <div
                    key={ci}
                    className="rounded-2xl border border-[#042133]/10 bg-white/80 p-5"
                  >
                    <div className="mb-4 h-4 w-40 animate-pulse rounded-full bg-[#042133]/8" />
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                      {[0, 1, 2, 3].map((n) => (
                        <SkeletonCard key={n} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700 text-sm">
            {error}
          </div>
        )}

        {!loading &&
          !error &&
          sections.map((section, index) => (
            <SectionBlock
              key={section.id}
              section={section}
              index={index}
              onVoteClick={() => setModalOpen(true)}
            />
          ))}
      </section>

      <BaselineAppModal open={modalOpen} onOpenChange={setModalOpen} />
    </SiteLayout>
  )
}
